import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NgControl } from '@angular/forms';
import { SelectOption, TemplateDirective } from '@fleet-beat/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject } from 'rxjs';
import { SimpleField } from '../../abstracts/simple-field';

@Component({
  selector: 'fb-checkbox-list-field',
  templateUrl: './checkbox-list-field.component.html',
  styleUrls: ['./checkbox-list-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxListFieldComponent extends SimpleField<string[]> implements OnInit {
  @Input()
  set options(value: SelectOption[]) {
    this.optionsSubject.next(value);
  }
  get options(): SelectOption[] {
    return this.optionsSubject.value;
  }

  @Input()
  @Input()
  direction: 'row' | 'column' = 'column';
  @Input() templateName?: string;
  @Input() cssClass?: string;
  @Input() inputHidden: boolean = false;

  form: FormGroup;

  @ContentChildren(TemplateDirective) templates: QueryList<TemplateDirective<object>>;

  private optionsSubject: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Self()
    public override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.optionsSubject.pipe(untilComponentDestroyed(this)).subscribe((options: SelectOption[]) => {
      this.fillForm(options);
    });
  }

  protected override onWriteValue(value: string[]): void {
    if (!this.form) {
      return;
    }
    if (!value?.length) return;
    const formValue: Record<string, boolean> = this.optionsSubject.value.reduce(
      (result: Record<string, boolean>, item: SelectOption) => {
        const key: string = `${item.value}`;
        result[key] = value.includes(key);
        return result;
      },
      {},
    );
    this.form.setValue(formValue);
  }

  private fillForm(options: SelectOption[]): void {
    if (!options) return;
    this.form = new FormGroup(
      options.reduce((result: Record<string, AbstractControl>, option: SelectOption) => {
        const key: string = `${option.value}`;
        result[key] = new FormControl(this.value?.includes(key) || false);
        return result;
      }, {}),
    );
    this.form.valueChanges.pipe(untilComponentDestroyed(this)).subscribe((values: Record<string, boolean>) => {
      this.value = Object.keys(values).filter((key: string) => Boolean(values[key]));
    });
  }
}
